import { useEffect, useState } from "react";
import { Check, X } from "lucide-react";
import SearchableSelect from "@/components/SearchableSelect";
import { loadProvinces, loadDistricts, codeByProvinceName } from "@/data/geo";

// Sağlayıcı hizmet bölgesi seçici. Çok il + çok ilçe. Değer, "İl / İlçe" biçiminde
// nitelikli string listesidir (ör. ["İzmir / Bornova", "Ankara / Çankaya"]) —
// backend eşleştirmesi bu anahtarı kullanır. Coğrafi veri lazy (public/geo) gelir.
export default function ProviderRegionPicker({ value = [], onChange }) {
  const [provinces, setProvinces] = useState([]);
  const [province, setProvince] = useState("");
  const [districts, setDistricts] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let a = true;
    loadProvinces().then((l) => { if (a) setProvinces(l); }).catch(() => {});
    return () => { a = false; };
  }, []);

  const code = codeByProvinceName(provinces, province);
  useEffect(() => {
    if (!code) { setDistricts([]); return; }
    let a = true;
    setLoading(true);
    loadDistricts(code)
      .then((l) => { if (a) setDistricts(l); })
      .catch(() => { if (a) setDistricts([]); })
      .finally(() => { if (a) setLoading(false); });
    return () => { a = false; };
  }, [code]);

  const key = (il, ilce) => `${il} / ${ilce}`;
  const toggleDistrict = (ilce) => {
    const k = key(province, ilce);
    onChange(value.includes(k) ? value.filter((x) => x !== k) : [...value, k]);
  };
  const removeRegion = (k) => onChange(value.filter((x) => x !== k));

  // Seçili bölgeleri il başına grupla.
  const grouped = value.reduce((acc, k) => {
    const il = k.split(" / ")[0];
    (acc[il] = acc[il] || []).push(k);
    return acc;
  }, {});

  return (
    <div className="space-y-4" data-testid="region-picker">
      {value.length > 0 && (
        <div className="space-y-2 rounded-2xl border border-slate-100 bg-slate-50/60 p-3">
          {Object.entries(grouped).map(([il, keys]) => (
            <div key={il} className="flex flex-wrap items-center gap-1.5">
              <span className="text-xs font-semibold text-slate-500 mr-1 min-w-[70px]">{il}</span>
              {keys.map((k) => (
                <button
                  type="button"
                  key={k}
                  onClick={() => removeRegion(k)}
                  data-testid={`region-chip-${k}`}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm bg-[#10B981] text-white hover:bg-[#0EA371]"
                >
                  {k.split(" / ")[1]} <X className="h-3.5 w-3.5" />
                </button>
              ))}
            </div>
          ))}
        </div>
      )}

      <div className="grid sm:grid-cols-[220px_1fr] gap-3 items-start">
        <SearchableSelect
          value={province}
          onChange={setProvince}
          options={provinces.map((p) => ({ value: p.name, label: p.name }))}
          placeholder="İl seçin"
          searchPlaceholder="İl ara..."
          className="h-11"
          testId="region-il-select"
        />

        <div className="flex flex-wrap gap-2 min-h-[2.75rem]">
          {!province && <p className="text-sm text-slate-400 py-2.5">Önce il seçin, sonra hizmet verdiğiniz ilçeleri işaretleyin.</p>}
          {province && loading && <p className="text-sm text-slate-400 py-2.5">Yükleniyor...</p>}
          {province && !loading && districts.map((d) => {
            const on = value.includes(key(province, d));
            return (
              <button
                type="button"
                key={d}
                onClick={() => toggleDistrict(d)}
                data-testid={`region-district-${d}`}
                className={`px-3.5 py-2 rounded-full text-sm font-medium border transition-all ${
                  on
                    ? "bg-[#10B981] text-white border-[#10B981]"
                    : "bg-white text-slate-700 border-slate-200 hover:border-[#10B981]"
                }`}
              >
                {on && <Check className="inline h-3.5 w-3.5 mr-1 -mt-0.5" />}{d}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
